import React from 'react';
import type { CourtReviewQueueItem } from './templateManagementService';

interface CourtReviewQueuePanelProps {
  items: CourtReviewQueueItem[];
  total: number;
  loading?: boolean;
  error?: string | null;
  actingItemId?: string | null;
  onApprove?: (item: CourtReviewQueueItem) => void;
  onReject?: (item: CourtReviewQueueItem) => void;
  onRefresh?: () => void;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString();
}

function severityBadgeClass(severity: string): string {
  const normalized = String(severity || '').toLowerCase();
  if (normalized === 'error') {
    return 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-200';
  }
  if (normalized === 'warning') {
    return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200';
  }
  return 'border-lex-accent bg-lex-accent-soft text-lex-accent dark:border-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent';
}

function statusBadgeClass(status: string): string {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'approved') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-900/20 dark:text-emerald-200';
  }
  if (normalized === 'rejected' || normalized === 'dismissed') {
    return 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-200';
  }
  if (normalized === 'in_review') {
    return 'border-lex-accent bg-lex-accent-soft text-lex-accent dark:border-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent';
  }
  return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200';
}

const CourtReviewQueuePanel: React.FC<CourtReviewQueuePanelProps> = ({
  items,
  total,
  loading = false,
  error = null,
  actingItemId = null,
  onApprove,
  onReject,
  onRefresh,
}) => {
  return (
    <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Court Intelligence Review Queue</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {loading ? 'Loading pending items...' : `${total} item${total === 1 ? '' : 's'} pending review`}
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={!onRefresh || loading}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-200">
          {error}
        </div>
      )}

      {!loading && items.length === 0 ? (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-4 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-300">
          No pending review items.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const canAction = item.status === 'pending' || item.status === 'in_review';
            const acting = actingItemId === item.id;
            return (
              <div
                key={item.id}
                className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/30"
              >
                <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{item.title}</p>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${severityBadgeClass(item.severity)}`}>
                      {item.severity.toUpperCase()}
                    </span>
                    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusBadgeClass(item.status)}`}>
                      {item.status.toUpperCase()}
                    </span>
                  </div>
                </div>

                {item.description && (
                  <p className="mb-2 text-sm text-gray-700 dark:text-gray-300">{item.description}</p>
                )}

                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Jurisdiction: {item.jurisdiction_code} • Type: {item.item_type} • Created: {formatDate(item.created_at)}
                </div>
                {item.reviewed_at && (
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Reviewed: {formatDate(item.reviewed_at)} {item.reviewed_by ? `by ${item.reviewed_by}` : ''}
                  </div>
                )}
                {item.resolution_notes && (
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Notes: {item.resolution_notes}</div>
                )}

                {canAction && (
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onApprove?.(item)}
                      disabled={!onApprove || acting}
                      className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-900/60 dark:bg-emerald-900/20 dark:text-emerald-200 dark:hover:bg-emerald-900/30"
                    >
                      {acting ? 'Working...' : 'Approve'}
                    </button>
                    <button
                      type="button"
                      onClick={() => onReject?.(item)}
                      disabled={!onReject || acting}
                      className="rounded-md border border-rose-300 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-rose-900/60 dark:bg-rose-900/20 dark:text-rose-200 dark:hover:bg-rose-900/30"
                    >
                      {acting ? 'Working...' : 'Reject'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CourtReviewQueuePanel;

